// Utility functions for data formatting and validation

export const formatDate = (dateString: string | null | undefined, format: 'short' | 'long' = 'short'): string => {
  if (!dateString || dateString === '0000-00-00 00:00:00' || dateString === '') {
    return 'Date unavailable';
  }
  
  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Date unavailable';
    }
    
    if (format === 'long') {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  } catch (error) {
    console.warn('Date formatting error:', error);
    return 'Date unavailable';
  }
};

export const safeString = (value: string | null | undefined, fallback: string = 'N/A'): string => {
  return value && value.trim() !== '' ? value : fallback;
};

export const safeArray = <T>(value: T[] | null | undefined): T[] => {
  return Array.isArray(value) ? value : [];
};

export const safeNumber = (value: number | null | undefined, fallback: number = 0): number => {
  return typeof value === 'number' && !isNaN(value) ? value : fallback;
};

export const formatCurrency = (amount: number | null | undefined, currency: string = 'USD'): string => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return 'Amount unavailable';
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

export const formatAddress = (address: string, city?: string, country?: string): string => {
  const parts = [address];
  
  if (city && city.trim() !== '') {
    parts.push(city);
  }
  
  if (country && country.trim() !== '') {
    parts.push(country);
  }
  
  return parts.join(', ');
};

export const validatePropertyData = (property: any): boolean => {
  if (!property || typeof property !== 'object') {
    return false;
  }
  
  // Check for required fields
  const requiredFields = ['id', 'name', 'type', 'address'];
  return requiredFields.every(field => property[field] !== null && property[field] !== undefined);
};

export const debugLogPropertyData = (property: any, context: string = ''): void => {
  if (process.env.NODE_ENV === 'development') {
    console.group(`🏠 Property Data Debug ${context ? `(${context})` : ''}`);
    console.log('Property Object:', property);
    console.log('Data Validation:', {
      hasId: property?.id !== undefined,
      hasName: property?.name !== undefined,
      hasAddress: property?.address !== undefined,
      hasCity: property?.city !== undefined,
      hasCountry: property?.country !== undefined,
      hasMobilePhone1: property?.mobile_phone_1 !== undefined,
      hasMobilePhone2: property?.mobile_phone_2 !== undefined,
      hasFacilities: Array.isArray(property?.facilities),
      hasSpecialSpecs: Array.isArray(property?.special_specs),
      hasValidCreatedAt: property?.created_at && !isNaN(new Date(property.created_at).getTime()),
      hasValidUpdatedAt: property?.updated_at && !isNaN(new Date(property.updated_at).getTime())
    });
    console.groupEnd();
  }
};
